import {
    Alert,
    Button,
    Card,
    CardContent,
    Divider,
    InputAdornment,
    Paper,
    Stack,
    TextField,
    Typography,
} from '@mui/material';
import { AutoAwesomeRounded, RemoveRedEyeRounded } from '@mui/icons-material';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import { useMemo } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import server from '../../../services/server';
import useErrors from '../../../hooks/useErrors';

const RECOVER_PASSWORD_URL = '/recuperar-senha?profile=club';

const schema = Yup.object().shape({
    name: Yup.string().required('Nome do clube é obrigatório'),
    ownerEmail: Yup.string().email('E-mail inválido').required('E-mail de acesso é obrigatório'),
    password: Yup.string()
        .required('Senha é obrigatória')
        .min(8, 'A senha deve ter no mínimo 8 caracteres'),
});

export function CreateClubStep({ contract, mutate, clubCreated }) {
    const { propostaId } = useParams();
    const { setErrors, enqueueSnackbar } = useErrors();
    const navigate = useNavigate();

    const term = contract?.contractData?.contract || {};
    const responsible = useMemo(
        () => ({
            name: term.contractorName || '—',
            document: term.contractorDocument || '—',
            email: term.contractorEmail || '',
            phone: term.contractorPhone || '—',
            position: term.contractorPosition || '—',
        }),
        [term],
    );

    const { values, handleSubmit, handleChange, errors, setFieldValue, isSubmitting } = useFormik({
        enableReinitialize: true,
        initialValues: {
            name: term.tradeName || term.companyName || '',
            ownerEmail: term.contractorEmail || '',
            password: '',
            showPassword: false,
        },
        validationSchema: schema,
        validateOnChange: true,
        validateOnMount: true,
        onSubmit: async (vals, { setSubmitting }) => {
            try {
                setSubmitting(true);
                await server.put(`subscription/${propostaId}/club`, {
                    name: vals.name.trim(),
                    ownerEmail: vals.ownerEmail.trim(),
                    password: vals.password,
                });
                await mutate();
                enqueueSnackbar('Clube criado com sucesso!');
            } catch (error) {
                setErrors(error);
            } finally {
                setSubmitting(false);
            }
        },
    });

    if (clubCreated) {
        return (
            <Paper variant="outlined" sx={{ p: 2 }}>
                <Stack spacing={2} alignItems="center" textAlign="center" py={2}>
                    <AutoAwesomeRounded sx={{ fontSize: 80, color: 'orange' }} />
                    <Typography variant="h3">Parabéns!</Typography>
                    <Divider sx={{ width: 150 }} />
                    <Typography variant="body1" fontWeight="bold">
                        Você agora faz parte da Eurobem Clubes de Benefícios.
                    </Typography>
                    <Typography variant="body2">
                        Enviamos um e-mail com as instruções para entrar no painel do clube. Use seu e-mail e
                        senha no login.
                    </Typography>
                    <Button variant="contained" onClick={() => navigate('/clube/login')}>
                        Acessar clube
                    </Button>
                </Stack>
            </Paper>
        );
    }

    return (
        <Paper variant="outlined" sx={{ p: 2 }}>
            <Stack spacing={2}>
                <Typography variant="h3" component="h2">
                    Crie seu clube
                </Typography>
                <Divider />
                <Typography variant="body2">
                    Os dados da pessoa jurídica e do responsável vêm do termo. Aqui você só define o nome do
                    clube e o acesso administrativo (e-mail e senha).
                </Typography>

                <TextField
                    label="Nome do clube"
                    name="name"
                    value={values.name}
                    onChange={handleChange}
                    error={!!errors.name}
                    helperText={errors.name}
                    placeholder="Nome do clube"
                />

                <Divider>Responsável (do termo)</Divider>

                <Card variant="outlined">
                    <CardContent sx={{ py: 2, '&:last-child': { pb: 2 } }}>
                        <Stack spacing={1}>
                            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={{ xs: 0.25, sm: 1 }}>
                                <Typography variant="body2" color="text.secondary" sx={{ minWidth: 110 }}>
                                    Nome
                                </Typography>
                                <Typography variant="body2" fontWeight={600}>
                                    {responsible.name}
                                </Typography>
                            </Stack>
                            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={{ xs: 0.25, sm: 1 }}>
                                <Typography variant="body2" color="text.secondary" sx={{ minWidth: 110 }}>
                                    Documento
                                </Typography>
                                <Typography variant="body2" fontWeight={600}>
                                    {responsible.document}
                                </Typography>
                            </Stack>
                            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={{ xs: 0.25, sm: 1 }}>
                                <Typography variant="body2" color="text.secondary" sx={{ minWidth: 110 }}>
                                    E-mail
                                </Typography>
                                <Typography variant="body2" fontWeight={600}>
                                    {responsible.email || '—'}
                                </Typography>
                            </Stack>
                            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={{ xs: 0.25, sm: 1 }}>
                                <Typography variant="body2" color="text.secondary" sx={{ minWidth: 110 }}>
                                    Telefone
                                </Typography>
                                <Typography variant="body2" fontWeight={600}>
                                    {responsible.phone}
                                </Typography>
                            </Stack>
                            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={{ xs: 0.25, sm: 1 }}>
                                <Typography variant="body2" color="text.secondary" sx={{ minWidth: 110 }}>
                                    Cargo
                                </Typography>
                                <Typography variant="body2" fontWeight={600}>
                                    {responsible.position}
                                </Typography>
                            </Stack>
                        </Stack>
                    </CardContent>
                </Card>

                <Divider>Acesso administrativo do clube</Divider>

                <Alert severity="info" sx={{ py: 1 }}>
                    <Typography variant="body2" component="div">
                        Informe o e-mail e a senha que você usará no painel do clube:
                    </Typography>
                    <Typography variant="body2" component="ul" sx={{ m: 0, pl: 2.5, mt: 0.5 }}>
                        <li>
                            Se você <strong>já tem</strong> uma conta com este e-mail, digite a senha atual —
                            ela será verificada e a conta será vinculada ao clube.
                        </li>
                        <li>
                            Se ainda <strong>não tem</strong> conta, a senha informada será usada para criar
                            o usuário (mínimo 8 caracteres).
                        </li>
                        <li>
                            Se o e-mail já existir e a senha estiver errada, corrija a senha ou use
                            &quot;Recuperar senha&quot;.
                        </li>
                    </Typography>
                </Alert>

                <TextField
                    label="E-mail de acesso"
                    name="ownerEmail"
                    value={values.ownerEmail}
                    onChange={handleChange}
                    error={!!errors.ownerEmail}
                    helperText={errors.ownerEmail || 'Este e-mail será o usuário de login do painel.'}
                    autoComplete="username"
                />

                <TextField
                    label="Senha"
                    name="password"
                    value={values.password}
                    onChange={handleChange}
                    type={values.showPassword ? 'text' : 'password'}
                    error={!!errors.password}
                    helperText={errors.password || 'Mínimo de 8 caracteres.'}
                    autoComplete="current-password"
                    InputProps={{
                        endAdornment: (
                            <InputAdornment
                                position="end"
                                onClick={() => setFieldValue('showPassword', !values.showPassword)}
                                sx={{ cursor: 'pointer' }}
                            >
                                <RemoveRedEyeRounded />
                            </InputAdornment>
                        ),
                    }}
                />

                <Stack
                    direction={{ xs: 'column', sm: 'row' }}
                    spacing={1}
                    justifyContent="space-between"
                    alignItems={{ xs: 'stretch', sm: 'center' }}
                >
                    <Button
                        variant="text"
                        href={RECOVER_PASSWORD_URL}
                        target="_blank"
                        rel="noopener noreferrer"
                        sx={{ alignSelf: { xs: 'flex-start', sm: 'center' } }}
                    >
                        Recuperar senha
                    </Button>
                    <Button
                        variant="contained"
                        onClick={handleSubmit}
                        loading={isSubmitting}
                        disabled={isSubmitting}
                    >
                        Criar meu clube
                    </Button>
                </Stack>
            </Stack>
        </Paper>
    );
}
